<section class="page-header">
  <div class="title-actions">
    <h1>Jobs</h1>
  </div>
  <div class="toolbar">
    <label class="search-field">
      <span class="field-icon search-icon" aria-hidden="true"></span>
      <input
        type="search"
        [(ngModel)]="searchTerm"
        (ngModelChange)="onSearchTermChange()"
        (keydown.enter)="filterLocalJobs($event)"
        [disabled]="fetchingRemoteJob"
        placeholder="Search job number..."
        aria-label="Search jobs by job number" />
      @if (fetchingRemoteJob) {
        <span class="lookup-status">Loading</span>
      }
    </label>
    <button type="button" class="lookup-button" (click)="lookupSearchJob()" [disabled]="!canFetchRemoteJob">
      {{ fetchingRemoteJob ? 'Loading...' : 'Get Job' }}
    </button>
    <label class="select-field filter-field">
      <svg class="field-icon filter-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M3 6H21V8H3V6ZM6 11H18V13H6V11ZM10 16H14V18H10V16Z" /></svg>
      <select [(ngModel)]="statusFilter" aria-label="Status filter">
        <option>All Jobs</option>
        <option>In Progress</option>
        <option>Completed</option>
        <option>Archived</option>
        <option>Pending</option>
      </select>
    </label>
    <label class="select-field sort-field">
      <span class="field-icon sort-icon" aria-hidden="true"></span>
      <select [(ngModel)]="sortBy" aria-label="Sort jobs">
        <option value="inspectionDateTime">Inspection Date/Time</option>
        <option value="address">Address</option>
        <option value="jobNumber">Job Number</option>
      </select>
    </label>
    <button type="button" class="sync-button" (click)="openDownloadJobs()" [disabled]="loadingAssignedJobs || downloadingSelectedJobs">
      <svg class="sync-download-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="M20 16.2A4.5 4.5 0 0 0 17.5 8h-1.1A7 7 0 0 0 3 10.6A4.5 4.5 0 0 0 4.5 19H6" />
        <path d="M12 12v9" />
        <path d="m8 17 4 4 4-4" />
      </svg>
      <span>{{ loadingAssignedJobs ? 'Loading...' : "Download Jobs" }}</span>
    </button>
    <button *ngIf="false" type="button" class="btn primary new-job-button" (click)="createJob()">New Job</button>
  </div>
</section>

@if (fetchingRemoteJob) {
  <div class="processing-backdrop" role="status" aria-live="polite" aria-label="Searching job">
    <section class="processing-panel">
      <span class="processing-spinner" aria-hidden="true"></span>
      <h2>Searching Job</h2>
      <p>Fetching job details and preparing the property form.</p>
    </section>
  </div>
}

@if (loading) {
  <div class="grid-list">
    @for (item of [1,2,3,4,5,6]; track item) {
      <div class="skeleton-card"></div>
    }
  </div>
} @else if (filteredJobs.length > 0) {
  <div class="jobs-grid" aria-label="Jobs">
    @for (job of filteredJobs; track job.id) {
      <app-job-card
        [job]="job"
        (edit)="editJob($event)"
        (markCompleted)="completeJob($event)"
        (archive)="archiveJob($event)"
        (delete)="deleteJob($event)" />
    }
  </div>
} @else {
  <div class="empty-state">
    <h2>{{ lookupErrorMessage || 'NZ Rural Report Not Found.' }}</h2>
    <p>Create a job or adjust the current search and filter.</p>
  </div>
}

@if (downloadJobsOpen) {
  <div class="modal-backdrop">
    <section class="modal-panel download-jobs-panel" [class.download-in-progress]="downloadingSelectedJobs" role="dialog" aria-modal="true" aria-label="Choose Jobs to Download to TOPS Offline">
      <button type="button" class="modal-close" (click)="closeDownloadJobs()" [disabled]="downloadingSelectedJobs" aria-label="Close">×</button>

      <div class="download-jobs-content" [class.is-blurred]="downloadingSelectedJobs">
        <header class="download-jobs-header">
          <h2>Choose Jobs to Download to TOPS Offline</h2>
          <div class="download-jobs-subheader">
            <p>Select the jobs you'd like to download to TOPS Offline.</p>
            @if (assignedJobs.length > 0) {
              <label class="select-all-download-jobs">
                <input
                  type="checkbox"
                  [checked]="areAllDownloadJobsSelected"
                  [disabled]="selectableDownloadJobCount === 0 || downloadingSelectedJobs"
                  (change)="toggleAllDownloadJobs()" />
                <span>Select all</span>
              </label>
            }
          </div>
        </header>

        @if (assignedJobs.length > 0) {
          <div class="download-jobs-grid" aria-label="Assigned jobs available to download">
            @for (details of assignedJobs; track details.trackerId) {
              <label class="download-job-card" [class.selected]="isDownloadJobSelected(details.jobNumber)" [class.disabled]="isJobAlreadyOffline(details.jobNumber)">
                <input
                  type="checkbox"
                  [checked]="isDownloadJobSelected(details.jobNumber)"
                  [disabled]="isJobAlreadyOffline(details.jobNumber) || downloadingSelectedJobs"
                  (change)="toggleDownloadJob(details.jobNumber)" />
                <span class="download-job-copy">
                  <strong>
                    {{ details.jobNumber }}
                    @if (isJobAlreadyOffline(details.jobNumber)) {
                      <em>(already offline)</em>
                    }
                  </strong>
                  <span>{{ details.address }}</span>
                  <small>{{ details.appointmentDate || 'No inspection date' }} {{ details.appointmentTime || '' }} · {{ details.reportType || 'Report' }}</small>
                </span>
              </label>
            }
          </div>
        } @else {
          <div class="download-jobs-empty">No assigned incomplete jobs were found.</div>
        }

        <footer class="modal-footer download-jobs-footer">
          <button type="button" class="btn secondary" (click)="closeDownloadJobs()" [disabled]="downloadingSelectedJobs">Cancel</button>
          <button type="button" class="btn primary" (click)="downloadSelectedJobs()" [disabled]="selectedDownloadJobNumbers.size === 0 || downloadingSelectedJobs">
            <svg class="sync-download-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path d="M20 16.2A4.5 4.5 0 0 0 17.5 8h-1.1A7 7 0 0 0 3 10.6A4.5 4.5 0 0 0 4.5 19H6" />
              <path d="M12 12v9" />
              <path d="m8 17 4 4 4-4" />
            </svg>
            {{ downloadingSelectedJobs ? 'Downloading...' : 'Download ' + selectedDownloadJobNumbers.size + ' Jobs' }}
          </button>
        </footer>
      </div>

      @if (downloadingSelectedJobs) {
        <div class="download-progress-overlay" role="status" aria-live="polite">
          <div class="download-progress-card">
            <span class="processing-spinner" aria-hidden="true"></span>
            <h3>Downloading Jobs</h3>
            <p>{{ downloadProgressMessage }}</p>
            <div class="download-progress-track" aria-label="Download progress">
              <span [style.width.%]="downloadProgressPercent"></span>
            </div>
            <strong>{{ downloadProgressCompleted }} of {{ downloadProgressTotal }} jobs downloaded</strong>
          </div>
        </div>
      }
    </section>
  </div>
}

@if (syncReviewOpen && syncDraft) {
  <div class="modal-backdrop">
    <section class="modal-panel sync-review-panel" role="dialog" aria-modal="true" aria-label="Review sync conflicts">
      <header class="modal-header">
        <h2>Review Sync Conflicts</h2>
      </header>
      <p class="sync-review-summary">Select which value to keep for Job {{ syncDraft.jobNumber || syncDraft.jobId }}.</p>

      <div class="conflict-list">
        @for (conflict of syncConflicts; track conflict.path) {
          <section class="conflict-row">
            <h3>{{ conflict.label }}</h3>
            <div class="conflict-values">
              <label class="conflict-choice">
                <input type="radio" value="server" [(ngModel)]="conflict.selected" />
                <span>Server</span>
                <pre>{{ valueText(conflict.serverValue) }}</pre>
              </label>
              <label class="conflict-choice">
                <input type="radio" value="local" [(ngModel)]="conflict.selected" />
                <span>Offline JSON</span>
                <pre>{{ valueText(conflict.localValue) }}</pre>
              </label>
            </div>
          </section>
        }
      </div>

      <footer class="modal-footer">
        <button type="button" class="btn secondary" (click)="closeSyncReview()" [disabled]="!!syncingJobId">Cancel</button>
        <button type="button" class="btn primary" (click)="confirmJobSync()" [disabled]="!!syncingJobId">{{ syncingJobId ? 'Syncing...' : 'Sync' }}</button>
      </footer>
    </section>
  </div>
}

<app-job-form-modal
  [open]="showJobModal"
  [job]="selectedJob"
  (closed)="closeJobModal()"
  (saved)="onJobSaved()"
  (prefill)="pendingPrefill = $event" />

@if (jobToDelete) {
  <div class="modal-backdrop">
    <section class="modal-panel confirm-panel" role="dialog" aria-modal="true" aria-label="Delete job">
      <header class="modal-header">
        <h2>Delete Job</h2>
      </header>
      <p>Delete {{ jobToDelete.address }}? This removes the job and related stored data.</p>
      <footer class="modal-footer">
        <button type="button" class="btn secondary" (click)="jobToDelete = null">Cancel</button>
        <button type="button" class="btn danger" (click)="confirmDelete()">Delete</button>
      </footer>
    </section>
  </div>
}
